import { StrengthTag, Tag, TagRow } from '@/components/matching/matchingTags';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { EXTRACTOR_ROLES } from '@rock/recalls';
import type { RecallEvidence, RecallMatchStrength, RecallPageUnavailable, RecallRuleSource } from '@rock/types';
import type { FC } from 'react';

export { CodeTag, StrengthTag, Tag, TagRow } from '@/components/matching/matchingTags';

/** One colour per model family the recall pipeline runs, matched on the name inside the full model id. */
const LLM_FAMILIES: { name: string; className: string }[] = [
    { name: 'sol', className: 'bg-sky-200 text-sky-900' },
    { name: 'luna', className: 'bg-cyan-200 text-cyan-900' },
    { name: 'sonnet', className: 'bg-orange-200 text-orange-900' },
    { name: 'haiku', className: 'bg-amber-200 text-amber-900' },
];

/** A model as a tag in its family colour; `short` shows the family name instead of the full id. */
export const LlmTag: FC<{ llm: string; short?: boolean }> = ({ llm, short }) => {
    const family = LLM_FAMILIES.find(({ name }) => llm.includes(name));
    return <Tag className={family?.className}>{short && family ? family.name : llm}</Tag>;
};

export const AdminTag: FC = () => <Tag className="bg-violet-200 text-violet-900">admin</Tag>;

/** Who put a rule or a brand name on the recall: one or both extractors, or an admin. */
export const SourceTags: FC<{ source: RecallRuleSource }> = ({ source }) =>
    source === 'admin' ? (
        <AdminTag />
    ) : (
        (source === 'both' ? EXTRACTOR_ROLES : [source]).map((llm) => <LlmTag key={llm} llm={llm} short />)
    );

export interface RuleFactsProps {
    source: RecallRuleSource;
    verified: boolean;
    accepted?: boolean;
    strength?: RecallMatchStrength;
    /** Where an admin addition came from, or an extractor's remark. */
    note?: string | null;
}

/** A rule's state as labelled tags, with a short note only where the tags don't say what it means for matching. */
export const RuleFacts: FC<RuleFactsProps> = ({ source, verified, accepted, strength, note }) => (
    <span className="flex max-w-xs flex-col gap-1">
        <TagRow label={source === 'admin' ? 'Added by' : 'Extracted by'}>
            <SourceTags source={source} />
        </TagRow>
        <TagRow label="Existence">
            {verified ? (
                <Tag className="bg-emerald-200 text-emerald-900">verified</Tag>
            ) : accepted ? (
                <Tag className="bg-sky-200 text-sky-900">accepted by admin</Tag>
            ) : (
                <Tag className="bg-amber-200 text-amber-900">unverified</Tag>
            )}
        </TagRow>
        {!verified && !accepted && <span>Not printed word for word; it matches once accepted.</span>}
        {!verified && accepted && <span>It matches because an admin accepted it.</span>}
        {note && <span className="italic">{note}</span>}
        {strength && (
            <TagRow label="Identifier">
                <StrengthTag strength={strength} />
            </TagRow>
        )}
        {strength === 'weak' && <span>Matches only units of the types in Applies To.</span>}
    </span>
);

/** The recall's web page could not be read, so its notice may lack the tables only the page has. */
export const PageUnavailableBadge: FC<{ pageUnavailable: RecallPageUnavailable }> = ({ pageUnavailable }) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-900">
                page not read
            </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
            The recall's web page could not be read ({pageUnavailable.reason}), so the notice has only the source data's
            text and may be missing tables of models or serial numbers. Re-process reads the page again.
        </TooltipContent>
    </Tooltip>
);

/** How a unit's or model's type sits beside the recall's list, when it is not simply listed. */
export function typeDisagreementLabel(
    evidence: Pick<RecallEvidence, 'typeMismatch' | 'typeFamily'> | undefined,
): string | undefined {
    if (evidence?.typeFamily) return 'same family';
    return evidence?.typeMismatch ? 'type differs' : undefined;
}

export const TypeDisagreementBadge: FC<{
    evidence: Pick<RecallEvidence, 'typeMismatch' | 'typeFamily'> | undefined;
}> = ({ evidence }) => {
    const label = typeDisagreementLabel(evidence);
    return label ? <Badge variant="secondary">{label}</Badge> : null;
};
